import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

/** Browser chrome color: Mineral White on the day canvas, Forest Night otherwise. */
export const THEME_COLOR: Record<Theme, string> = { light: '#F7F7F0', dark: '#0F1D16' };

const STORAGE_KEY = 'theme';
const EVENT = 'themechange';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Current theme, as set on <html> (before first paint, by the inline script of index.html). */
export function getTheme(): Theme {
	return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function stored(): Theme | null {
	try {
		const value = window.localStorage.getItem(STORAGE_KEY);
		return value === 'dark' || value === 'light' ? value : null;
	} catch {
		return null;
	}
}

/** Theme color of the browser UI: dark whenever the page is dark, including the dusk chapter. */
export function syncThemeColor(): void {
	const root = document.documentElement;
	const dark = root.dataset.theme === 'dark' || root.dataset.chapter === 'dusk';
	document
		.querySelector('meta[name="theme-color"]')
		?.setAttribute('content', THEME_COLOR[dark ? 'dark' : 'light']);
}

/** Set the theme on <html>, optionally persist it, and tell the listeners (WebGL fibers, dusk tween). */
export function applyTheme(theme: Theme, persist: boolean): void {
	const root = document.documentElement;
	const changed = root.dataset.theme !== theme;
	root.dataset.theme = theme;
	root.style.colorScheme = theme;
	syncThemeColor();
	if (persist) {
		try {
			window.localStorage.setItem(STORAGE_KEY, theme);
		} catch {
			/* Private mode: the choice just lasts for the session. */
		}
	}
	if (changed) window.dispatchEvent(new CustomEvent<Theme>(EVENT, { detail: theme }));
}

function subscribe(onChange: () => void): () => void {
	const query = window.matchMedia(DARK_QUERY);
	// Without an explicit choice, follow the system preference live.
	const onSystem = () => {
		if (!stored()) applyTheme(query.matches ? 'dark' : 'light', false);
	};
	window.addEventListener(EVENT, onChange);
	query.addEventListener('change', onSystem);
	return () => {
		window.removeEventListener(EVENT, onChange);
		query.removeEventListener('change', onSystem);
	};
}

/** Theme state shared by every consumer (a single source of truth: the <html> attribute). */
export function useTheme(): { theme: Theme; toggle: () => void } {
	const theme = useSyncExternalStore(subscribe, getTheme, () => 'light' as Theme);
	const toggle = useCallback(() => applyTheme(getTheme() === 'dark' ? 'light' : 'dark', true), []);
	return { theme, toggle };
}
